/**
 * 画像キャッシュユーティリティ
 *
 * IndexedDB に画像 Blob を LRU + TTL で永続キャッシュする。
 * AuthImage の cacheKey prop と連携して使用する。
 */

import { db, StoredImageCache } from '@/lib/db'

/** キャッシュ上限エントリ数 */
const MAX_ENTRIES = 200

/** TTL: 7日間 */
const TTL_MS = 7 * 24 * 60 * 60 * 1000

/**
 * キャッシュから画像を取得し Object URL を返す。
 * TTL 切れの場合は null を返しエントリを削除する。
 * ヒット時に lastAccessedAt を更新する（LRU）。
 */
export const getImageFromCache = async (key: string): Promise<string | null> => {
  try {
    const entry = await db.imageCache.get(key)
    if (!entry) return null

    // TTL チェック
    if (Date.now() - entry.cachedAt > TTL_MS) {
      await db.imageCache.delete(key)
      return null
    }

    // LRU: lastAccessedAt を更新
    db.imageCache.update(key, { lastAccessedAt: Date.now() })

    return URL.createObjectURL(entry.blob)
  } catch {
    return null
  }
}

/**
 * 画像 Blob をキャッシュに保存する。
 * 上限超過時は lastAccessedAt が最も古いエントリから削除する。
 */
export const cacheImageBlob = async (key: string, blob: Blob): Promise<void> => {
  try {
    const now = Date.now()
    const entry: StoredImageCache = {
      key,
      blob,
      cachedAt: now,
      lastAccessedAt: now,
    }

    await db.imageCache.put(entry)

    // 上限チェック（非同期で eviction）
    const count = await db.imageCache.count()
    if (count > MAX_ENTRIES) {
      const excess = count - MAX_ENTRIES
      const oldest = await db.imageCache.orderBy('lastAccessedAt').limit(excess).primaryKeys()
      await db.imageCache.bulkDelete(oldest)
    }
  } catch {
    // キャッシュ保存失敗は無視（非クリティカル）
  }
}

/**
 * 指定キーのキャッシュを削除する。
 */
export const invalidateImageCache = async (key: string): Promise<void> => {
  try {
    await db.imageCache.delete(key)
  } catch {
    // 無視
  }
}

/**
 * TTL 切れのエントリを一括削除する（アプリ起動時に呼び出す想定）。
 */
export const cleanupExpiredImageCache = async (): Promise<void> => {
  try {
    const expiredBefore = Date.now() - TTL_MS
    await db.imageCache.where('lastAccessedAt').below(expiredBefore).delete()
  } catch {
    // 無視
  }
}
